import React, { useState, useRef } from 'react';
import Icon from 'components/AppIcon';

const CINEMATIC_EFFECTS = [
  { id: 'camera-pan', label: 'Camera Pan', icon: 'Move', color: '#6366F1', desc: 'Smooth horizontal camera movement' },
  { id: 'zoom-in', label: 'Zoom In', icon: 'ZoomIn', color: '#8B5CF6', desc: 'Dramatic zoom into subject' },
  { id: 'lighting', label: 'Dynamic Lighting', icon: 'Sun', color: '#F59E0B', desc: 'Cinematic light shifts and rays' },
  { id: 'particles', label: 'Particle Motion', icon: 'Sparkles', color: '#10B981', desc: 'Floating particles and dust' },
  { id: 'depth', label: 'Depth Movement', icon: 'Layers', color: '#06B6D4', desc: 'Parallax depth effect' },
  { id: 'environment', label: 'Environmental', icon: 'Wind', color: '#EC4899', desc: 'Wind, rain, snow effects' },
];

const ANIMATION_STYLES = [
  { id: 'cinematic', label: 'Cinematic', color: '#6366F1' },
  { id: 'dreamy', label: 'Dreamy', color: '#8B5CF6' },
  { id: 'dramatic', label: 'Dramatic', color: '#EF4444' },
  { id: 'peaceful', label: 'Peaceful', color: '#10B981' },
];

const GENERATION_STEPS = [
  'Analyzing image composition...',
  'Detecting scene elements...',
  'Applying cinematic effects...',
  'Generating motion frames...',
  'Adding lighting effects...',
  'Rendering final animation...',
];

export default function ImageToVideoWorkspace({ onGenerated, disabled }) {
  const [uploadedImage, setUploadedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedEffects, setSelectedEffects] = useState(['camera-pan']);
  const [animationStyle, setAnimationStyle] = useState('cinematic');
  const [duration, setDuration] = useState(5);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [stepIndex, setStepIndex] = useState(0);
  const [result, setResult] = useState(null);
  const imageInputRef = useRef(null);
  const progressRef = useRef(null);
  const stepRef = useRef(null);

  const handleImageUpload = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      setImagePreview(e?.target?.result);
      setUploadedImage(file);
    };
    reader?.readAsDataURL(file);
  };

  const toggleEffect = (effectId) => {
    setSelectedEffects((prev) =>
      prev?.includes(effectId) ? prev?.filter((e) => e !== effectId) : [...prev, effectId]
    );
  };

  const handleGenerate = () => {
    if (!imagePreview || isGenerating) return;
    setIsGenerating(true);
    setProgress(0);
    setStepIndex(0);
    setResult(null);

    let current = 0;
    progressRef.current = setInterval(() => {
      current += 1.2;
      if (current >= 90) { current = 90; clearInterval(progressRef?.current); }
      setProgress(current);
    }, 130);

    stepRef.current = setInterval(() => {
      setStepIndex((prev) => (prev < GENERATION_STEPS?.length - 1 ? prev + 1 : prev));
    }, 1800);

    setTimeout(() => {
      clearInterval(progressRef?.current);
      clearInterval(stepRef?.current);
      setProgress(100);
      const generated = {
        id: Date.now(),
        title: `Animated Scene - ${animationStyle}`,
        style: animationStyle,
        effects: selectedEffects,
        duration: `${duration}s`,
        thumbnail: imagePreview,
        mode: 'image-to-video',
        createdAt: new Date()?.toLocaleDateString('en-US'),
      };
      setResult(generated);
      setIsGenerating(false);
      onGenerated?.(generated);
    }, 11000);
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Image upload */}
      <div>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Upload Image to Animate</p>
        <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e?.target?.files?.[0])} />
        {imagePreview ? (
          <div className="relative rounded-xl overflow-hidden" style={{ height: 140 }}>
            <img src={imagePreview} alt="Image to animate" className="w-full h-full object-cover" />
            <div className="absolute inset-0 flex items-end p-2" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, transparent 60%)' }}>
              <div className="flex items-center gap-2 w-full">
                <div className="flex items-center gap-1.5 px-2 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.9)' }}>
                  <Icon name="CheckCircle2" size={10} color="white" />
                  <span className="text-xs font-semibold text-white" style={{ fontFamily: 'var(--font-caption)' }}>Image ready</span>
                </div>
                <button
                  onClick={() => { setImagePreview(null); setUploadedImage(null); }}
                  className="ml-auto w-6 h-6 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(239,68,68,0.9)' }}
                >
                  <Icon name="X" size={11} color="white" />
                </button>
              </div>
            </div>
          </div>
        ) : (
          <button
            onClick={() => imageInputRef?.current?.click()}
            disabled={disabled || isGenerating}
            className="w-full flex flex-col items-center gap-2 py-6 rounded-xl border-2 border-dashed transition-all hover:opacity-80 disabled:opacity-50"
            style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)' }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.12)' }}>
              <Icon name="ImagePlus" size={20} color="var(--color-primary)" />
            </div>
            <div className="text-center">
              <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Upload Image</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>JPG, PNG, WebP — single image to animate</p>
            </div>
          </button>
        )}
      </div>
      {/* Animation style */}
      <div>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Animation Style</p>
        <div className="flex gap-2 flex-wrap">
          {ANIMATION_STYLES?.map((style) => (
            <button
              key={style?.id}
              onClick={() => !isGenerating && setAnimationStyle(style?.id)}
              disabled={isGenerating}
              className="px-3 py-1.5 rounded-full border text-xs font-semibold transition-all"
              style={{
                borderColor: animationStyle === style?.id ? style?.color : 'var(--color-border)',
                background: animationStyle === style?.id ? `${style?.color}15` : 'var(--color-card)',
                color: animationStyle === style?.id ? style?.color : 'var(--color-muted-foreground)',
                fontFamily: 'var(--font-caption)',
              }}
            >
              {style?.label}
            </button>
          ))}
        </div>
      </div>
      {/* Cinematic effects */}
      <div>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Cinematic Effects</p>
        <div className="grid grid-cols-2 gap-2">
          {CINEMATIC_EFFECTS?.map((effect) => {
            const active = selectedEffects?.includes(effect?.id);
            return (
              <button
                key={effect?.id}
                onClick={() => !isGenerating && toggleEffect(effect?.id)}
                disabled={isGenerating}
                className="flex items-center gap-2 p-2.5 rounded-xl border text-left transition-all"
                style={{
                  borderColor: active ? effect?.color : 'var(--color-border)',
                  background: active ? `${effect?.color}10` : 'var(--color-card)',
                }}
              >
                <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: active ? effect?.color : `${effect?.color}20` }}>
                  <Icon name={effect?.icon} size={13} color={active ? '#fff' : effect?.color} />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-semibold" style={{ color: active ? effect?.color : 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{effect?.label}</p>
                  <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)', fontSize: 9 }}>{effect?.desc}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      {/* Duration */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Duration</p>
          <span className="text-xs font-bold" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-data)' }}>{duration}s</span>
        </div>
        <input
          type="range" min={3} max={15} step={1} value={duration}
          onChange={(e) => setDuration(Number(e?.target?.value))}
          disabled={isGenerating}
          className="w-full accent-[var(--color-primary)]"
        />
        <div className="flex justify-between text-xs mt-1" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>
          <span>3s</span><span>15s</span>
        </div>
      </div>
      {/* Generate button */}
      <button
        onClick={handleGenerate}
        disabled={!imagePreview || isGenerating || disabled}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: 'white', fontFamily: 'var(--font-heading)' }}
      >
        <Icon name={isGenerating ? 'Loader' : 'Clapperboard'} size={16} color="white" />
        {isGenerating ? 'Animating Image...' : 'Animate Image'}
      </button>
      {/* Progress */}
      {isGenerating && (
        <div className="rounded-2xl p-4 flex flex-col gap-3 animate-fade-in-up" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid var(--color-border)' }}>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg flex items-center justify-center pulse-glow" style={{ background: 'var(--color-primary)' }}>
              <Icon name="Film" size={14} color="white" />
            </div>
            <div>
              <p className="text-xs font-semibold" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>Animating your image...</p>
              <p className="text-xs" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-caption)' }}>{GENERATION_STEPS?.[stepIndex]}</p>
            </div>
          </div>
          <div className="h-2 rounded-full overflow-hidden" style={{ background: 'var(--color-muted)' }}>
            <div className="h-full rounded-full transition-all duration-500" style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #6366F1, #8B5CF6)' }} />
          </div>
          <p className="text-xs text-right" style={{ color: 'var(--color-muted-foreground)', fontFamily: 'var(--font-data)' }}>{Math.round(progress)}%</p>
        </div>
      )}
      {/* Result */}
      {result && (
        <div className="rounded-2xl p-4 animate-fade-in-up" style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.3)' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl overflow-hidden flex-shrink-0">
              <img src={result?.thumbnail} alt="Animated result" className="w-full h-full object-cover" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold truncate" style={{ color: 'var(--color-foreground)', fontFamily: 'var(--font-heading)' }}>{result?.title}</p>
              <p className="text-xs" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-caption)' }}>Image Animation · {result?.duration}</p>
            </div>
            <div className="flex items-center gap-1 px-2 py-1 rounded-full" style={{ background: 'rgba(16,185,129,0.12)' }}>
              <Icon name="CheckCircle2" size={11} color="var(--color-success)" />
              <span className="text-xs font-semibold" style={{ color: 'var(--color-success)', fontFamily: 'var(--font-caption)' }}>Ready</span>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[{ icon: 'Play', label: 'Preview' }, { icon: 'Download', label: 'Download' }, { icon: 'Shuffle', label: 'Remix' }]?.map((btn) => (
              <button
                key={btn?.label}
                className="flex flex-col items-center gap-1 py-2 rounded-xl border text-xs font-medium transition-all hover:opacity-80"
                style={{ borderColor: 'var(--color-border)', background: 'var(--color-muted)', color: 'var(--color-foreground)', fontFamily: 'var(--font-caption)' }}
              >
                <Icon name={btn?.icon} size={13} color="var(--color-muted-foreground)" />
                {btn?.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
